import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useLocalization } from '@/hooks/useLocalization';

type RiskType = 'bias' | 'drift' | 'hallucination' | 'privacy' | 'security' | 'other';
type RiskStatus = 'open' | 'investigating' | 'mitigated' | 'closed';
type Severity = 'low' | 'medium' | 'high' | 'critical';

interface AIRiskEntry {
  id: string;
  title: string;
  risk_type: RiskType;
  severity: Severity;
  status: RiskStatus;
  description: string | null;
  mitigation: string | null;
  created_at: string;
}

const TYPE_LABELS: Record<RiskType, string> = {
  bias:          'Bias',
  drift:         'Model Drift',
  hallucination: 'Hallucination',
  privacy:       'Privacy',
  security:      'Security',
  other:         'Other',
};

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'bg-destructive text-destructive-foreground',
  high:     'bg-amber-500 text-white',
  medium:   'bg-amber-400/80 text-amber-900',
  low:      'bg-muted text-muted-foreground',
};

const STATUS_COLORS: Record<RiskStatus, string> = {
  open:          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  investigating: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  mitigated:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed:        'bg-muted text-muted-foreground',
};

const emptyForm = { title: '', risk_type: 'other' as RiskType, severity: 'medium' as Severity, status: 'open' as RiskStatus, description: '', mitigation: '' };

export const AIRiskRegister = () => {
  const { tx, t } = useLocalization();
  const { tenantId } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<AIRiskEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('ai_risk_entries')
      .select('*')
      .order('created_at', { ascending: false });
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (!error) setEntries((data ?? []) as AIRiskEntry[]);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast({ title: t('Title required'), variant: 'destructive' }); return; }
    setSaving(true);
    const { error } = await supabase.from('ai_risk_entries').insert({
      title: form.title.trim(),
      risk_type: form.risk_type,
      severity: form.severity,
      status: form.status,
      description: form.description || null,
      mitigation: form.mitigation || null,
      tenant_id: tenantId,
    });
    setSaving(false);
    if (error) { toast({ title: t('Save failed'), description: error.message, variant: 'destructive' }); return; }
    toast({ title: t('Risk entry logged') });
    setDialogOpen(false);
    setForm(emptyForm);
    load();
  };

  const openCount  = entries.filter(e => e.status === 'open').length;
  const highCount  = entries.filter(e => e.severity === 'high' || e.severity === 'critical').length;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t('Total Entries')}</p><p className="text-2xl font-bold mt-1">{entries.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t('Open')}</p><p className="text-2xl font-bold mt-1 text-red-500">{openCount}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t('High / Critical')}</p><p className="text-2xl font-bold mt-1 text-amber-500">{highCount}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">{t('AI Risk Register')}</CardTitle>
            <CardDescription>{t('Log bias, drift, hallucination, and other AI-specific incidents')}</CardDescription>
          </div>
          <Button size="sm" className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />  {t('Log Risk')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mb-3 opacity-30" />
              <p className="text-sm">{t('No AI risk entries yet')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Risk')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Type')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Severity')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Status')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Logged')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <p className="font-medium">{tx(e.title)}</p>
                      {e.description && <p className="text-xs text-muted-foreground truncate max-w-xs mt-0.5">{tx(e.description)}</p>}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="outline" className="text-xs">{TYPE_LABELS[e.risk_type]}</Badge>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${SEVERITY_COLORS[e.severity]}`}>{tx(e.severity)}</span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[e.status]}`}>{tx(e.status.replace('_', ' '))}</span>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">{format(new Date(e.created_at), 'MMM d, yyyy')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('Log AI Risk Entry')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>{t('Title *')}</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('Brief description of the risk')} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>{t('Type')}</Label>
                <Select value={form.risk_type} onValueChange={v => setForm(f => ({ ...f, risk_type: v as RiskType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bias">{t('Bias')}</SelectItem>
                    <SelectItem value="drift">{t('Model Drift')}</SelectItem>
                    <SelectItem value="hallucination">{t('Hallucination')}</SelectItem>
                    <SelectItem value="privacy">{t('Privacy')}</SelectItem>
                    <SelectItem value="security">{t('Security')}</SelectItem>
                    <SelectItem value="other">{t('Other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('Severity')}</Label>
                <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v as Severity }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('Low')}</SelectItem>
                    <SelectItem value="medium">{t('Medium')}</SelectItem>
                    <SelectItem value="high">{t('High')}</SelectItem>
                    <SelectItem value="critical">{t('Critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('Status')}</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v as RiskStatus }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{t('Open')}</SelectItem>
                    <SelectItem value="investigating">{t('Investigating')}</SelectItem>
                    <SelectItem value="mitigated">{t('Mitigated')}</SelectItem>
                    <SelectItem value="closed">{t('Closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>{t('Description')}</Label><Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('What happened? How was it detected?')} /></div>
            <div className="space-y-2"><Label>{t('Mitigation Plan')}</Label><Textarea rows={2} value={form.mitigation} onChange={e => setForm(f => ({ ...f, mitigation: e.target.value }))} placeholder={t('Steps taken or planned to address this risk')} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}{t('Log Risk')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIRiskRegister;
